import { Main as Container } from 'Components/Main';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { GenerateNewReportRopa } from './Details';
import { FormSuccess } from 'Components/Form/Success';
import { useSelector } from 'react-redux';
import { useAuthTenant } from 'store/auth';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { addRopa } from 'services/report-ropa-service';
import { PageHelper } from 'Components/PageHelper';
import styled from 'styled-components';
import { Dialog, PageHeader } from 'Components/ui';

const INK = 'var(--vo-ink)';
const MUTED = 'var(--vo-slate)';

const FormCard = styled.div`
  max-width: 560px;
  background: white;
  border-radius: 14px;
  padding: 28px;
  box-shadow: 0 1px 3px var(--vo-escuro-8);
`;
const CardTitle = styled.p`
  font-family: 'Rubik', sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: ${INK};
  margin: 0 0 4px;
`;
const CardSubtitle = styled.p`
  font-family: 'Lato', sans-serif;
  font-size: 13px;
  color: ${MUTED};
  margin: 0 0 24px;
`;

export const ReportsRopaForm = () => {
  const { t } = useTranslation(['dashboard', 'common']);
  const selectedTenant = useSelector(useAuthTenant);
  const navigate = useNavigate();
  const [finished, setFinished] = useState(false);
  const {
    register,
    handleSubmit,
    formState: { errors },
    control,
    getValues,
    watch,
    reset,
  } = useForm({
    defaultValues: {
      name: '',
      scope: '',
      areaId: '',
      processId: '',
      companyKey: selectedTenant.slug,
    },
  });

  const handleComplete = async () => {
    try {
      const res = await addRopa(getValues());
      if (res.status === 200 || res.status === 201) {
        setFinished(true);
      } else {
        throw new Error(t('dashboard:ropa.form.errorSave'));
      }
    } catch (error) {
      Dialog.fire({
        title: t('dashboard:alert.errorTitle'),
        html: error.response?.data?.message ?? error.message,
        icon: 'error',
      });
    }
  };

  const handleReset = () => {
    reset();
    setFinished(false);
  };

  return (
    <Container>
      <PageHelper steps={tourSteps} tourKey="reports-ropa-form" />

      <PageHeader
        title={t('dashboard:ropa.form.title')}
        segments={[
          { label: t('common:breadcrumbs.reports') },
          { label: t('dashboard:ropa.breadcrumbLabel'), route: '/reports/ropa' },
          { label: t('dashboard:ropa.form.title') },
        ]}
      />

      <FormCard>
        {!finished && (
          <>
            <CardTitle>{t('dashboard:ropa.form.title')}</CardTitle>
            <CardSubtitle>{t('dashboard:ropa.form.subtitle')}</CardSubtitle>
            <GenerateNewReportRopa
              control={control}
              register={register}
              handleComplete={handleComplete}
              handleSubmit={handleSubmit}
              errors={errors}
              watch={watch}
              selectedTenant={selectedTenant}
            />
          </>
        )}
        {finished && (
          <FormSuccess
            title={t('dashboard:ropa.form.successTitle')}
            labelStartAgain={t('dashboard:ropa.form.startAgain')}
            handleStartAgain={() => handleReset()}
            labelShow="Ok"
            handleShow={() => navigate('/reports/ropa')}
          />
        )}
      </FormCard>
    </Container>
  );
};

const tourSteps = [
  {
    target: 'body',
    content: 'Bem-vindo ao formulário de geração de Relatório ROPA! Este relatório documenta todas as operações de processamento de dados conforme exigido pela LGPD.',
    disableBeacon: true,
    disableScrolling: true,
    placement: 'center',
  },
  {
    target: '[data-tour="report-name"]',
    content: 'Digite um nome descritivo para identificar este relatório ROPA.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="report-scope"]',
    content: 'Selecione o escopo do relatório: Empresa, Área, Processo ou Sumário Executivo.',
    disableScrolling: true,
  },
  {
    target: '[data-tour="form-actions"]',
    content: 'Use "Cancelar" para descartar as alterações ou "Gerar relatório" para criar o relatório ROPA.',
    disableScrolling: true,
  },
];
